// 注文書リソースの宣言。既存の purchase-order.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フロー】注文書（outbound=発注書/inbound=客先注文書）は案件（受注）に紐づけて作成する。
// 金額は明細から計算（税計算モードは案件から継承）。取引先IDと帳票番号は本体サービスが採番する。
import { z } from 'zod';
import {
  fetchPurchaseOrders,
  createPurchaseOrder,
  updatePurchaseOrder,
  type PurchaseOrderCreateInput,
  type PurchaseOrderUpdateInput,
} from '@modules/order/services/purchase-order.service';
import { findOrderByCode } from './order.resource';
import type { ID, PurchaseOrder } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiDocItemSchema,
  calcDocTotals,
  dateString,
  normalizeCode,
  resolveEmployee,
  toDateString,
  toDocLineItems,
  toTimestamp,
} from './shared';

/** 注文書1件を、LLM が読む最小限の形へ射影する。 */
function toCompactPurchaseOrder(p: PurchaseOrder) {
  return {
    code: p.code,
    type: p.type, // outbound=発注書 / inbound=客先注文書
    total: p.total, // 税込合計
    createdAt: toDateString(p.createdAt),
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const orders = await fetchPurchaseOrders();

  const keyword = filters.keyword;
  const filtered = keyword
    ? orders.filter((p) => p.code.includes(keyword) || (p.notes ?? '').includes(keyword))
    : orders;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    purchase_orders: filtered.slice(0, MAX_ROWS).map(toCompactPurchaseOrder),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const orders = await fetchPurchaseOrders();
  const found = orders.find((p) => normalizeCode(p.code) === code);
  if (!found) {
    return { found: false, note: `注文書番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, purchase_order: toCompactPurchaseOrder(found) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  orderCode: z.string().min(1, 'orderCode（案件番号）は必須。案件が無ければ先に create_record(order) で受注を作成する'),
  type: z.enum(['outbound', 'inbound']),
  items: z.array(aiDocItemSchema).min(1).max(30),
  notes: z.string().optional(),
  assignee: z.string().min(1).optional(),
  deliveryDate: dateString.optional(),
});

const writeUpdateSchema = z
  .object({
    deliveryDate: dateString.optional(),
    notes: z.string().optional(),
    assignee: z.string().min(1).optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(aiDocItemSchema).min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/** code から更新対象の注文書を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<PurchaseOrder | null> {
  const code = normalizeCode(rawCode);
  const orders = await fetchPurchaseOrders();
  return orders.find((p) => normalizeCode(p.code) === code) ?? null;
}

export const purchaseOrderResource: ResourceDescriptor = {
  name: 'purchase_order',
  label: '注文書',
  list,
  get,
  write: {
    fields: [
      { name: 'orderCode', label: '案件番号', type: 'ref', resource: 'order', required: true, note: '作成時のみ・案件番号。無ければ先に create_record(order)' },
      { name: 'type', label: '種別', type: 'enum', values: ['outbound', 'inbound'], required: true, note: '作成時のみ・outbound=発注書/inbound=客先注文書（更新では変更不可）' },
      { name: 'items', label: '明細', type: 'items', required: true, note: '配列 [{name,quantity,unit?,unitPrice}]。更新では全置き換え・金額は自動計算' },
      { name: 'notes', label: '備考', type: 'string' },
      { name: 'assignee', label: '担当者', type: 'string', note: '担当者名で指定（IDは不要）' },
      { name: 'deliveryDate', label: '納品予定日', type: 'date', note: 'YYYY-MM-DD' },
    ],
    createSchema: writeCreateSchema,
    // 種別(type)は本体 UpdateInput が Omit しているため更新では開放しない
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 案件（受注）を解決する（帳票番号 = 案件番号-枝番。案件なしの注文書は作らない）
      const order = await findOrderByCode(input.orderCode);
      if (!order) {
        throw new Error(
          `案件「${input.orderCode}」が見つかりません。先に create_record(order) で受注を作成し、その code を orderCode に指定してください`
        );
      }

      // ② 金額は明細から計算（税計算モードは案件から継承。標準税率のみ）
      const taxCalcMode = order.taxCalcMode ?? 'floor';
      const totals = calcDocTotals(input.items, taxCalcMode);

      // ③ 作成（採番・取引先ID・監査ログは本体サービスの責務）
      const created = await createPurchaseOrder(
        {
          type: input.type,
          orderId: order.id,
          // 担当者は名前→ID解決。指定が無ければ実行者を自動設定する（人がレビュー時に変更できる）
          assignedUserId: input.assignee !== undefined ? (await resolveEmployee(input.assignee)).id : userId,
          ...(input.deliveryDate !== undefined ? { deliveryDate: toTimestamp(input.deliveryDate) } : {}),
          lineItems: toDocLineItems(input.items),
          subtotal: totals.subtotal,
          taxAmount: totals.taxAmount,
          total: totals.total,
          taxCalcMode,
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
          aiPendingReview: true,
        } as PurchaseOrderCreateInput,
        userId
      );
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const patch: PurchaseOrderUpdateInput = { aiPendingReview: true };
      if (input.deliveryDate !== undefined) patch.deliveryDate = toTimestamp(input.deliveryDate);
      if (input.notes !== undefined) patch.notes = input.notes;
      // 担当者は名前→ID解決（IDをLLMに組ませない）
      if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;

      // 明細は「全置き換え」。金額（subtotal/taxAmount/total）は明細から再計算する
      if (input.items && input.items.length > 0) {
        const taxCalcMode = target.taxCalcMode ?? 'floor';
        const totals = calcDocTotals(input.items, taxCalcMode);
        patch.lineItems = toDocLineItems(input.items);
        patch.subtotal = totals.subtotal;
        patch.taxAmount = totals.taxAmount;
        patch.total = totals.total;
      }

      await updatePurchaseOrder(target.id, patch, userId);
      return { id: target.id, code: target.code };
    },
    link: (id) => `/purchase-orders/${id}/edit`,
  },
};
